import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError } from "rxjs/operators";
import { ActivatedRoute } from "@angular/router";
import { PortfolioService } from "../../../portfolio/portfolio.service";
import { HeatmapComponent } from "../../charts/heatmap.component";
import { CovGraphComponent } from "../../charts/cov-graph.component";
import { FrontierChartComponent } from "../../charts/frontier-chart.component";
import { DivergentCellComponent } from "../../charts/divergent-cell.component";
import { TransitionMapComponent } from "../../charts/transition-map.component";
import {
  HbarListComponent,
  HBarRow,
} from "../../../../charts/hbar-list/hbar-list.component";
import { FrontierPoint } from "../../models/experience.model";

interface ForecastedRow {
  ticker: string;
  name: string;
  weight: number;
  historical: number;
  implied: number;
  marketEq: number;
  forecast: number;
}

@Component({
  selector: "app-exp-optimize",
  standalone: true,
  imports: [
    FormsModule,
    HeatmapComponent,
    CovGraphComponent,
    FrontierChartComponent,
    DivergentCellComponent,
    TransitionMapComponent,
    HbarListComponent,
  ],
  templateUrl: "./optimize-experience.component.html",
})
export class OptimizeExperienceComponent implements OnInit, OnDestroy {
  portfolio: any = null;
  optimizationData: any = null;
  evaluationData: any = null;
  plotData: any = null;
  covMatrix: { labels: string[]; matrix: number[][] } = {
    labels: [],
    matrix: [],
  };
  corrMatrix: { labels: string[]; matrix: number[][] } = {
    labels: [],
    matrix: [],
  };
  topWeights: number[] = [];
  forecastRows: ForecastedRow[] = [];
  maxForecastAbs = 0.05;
  combinedSummary = { historical: 0, implied: 0, marketEq: 0, forecast: 0 };
  views: Array<{
    id: string;
    name: string;
    type: string;
    confidence?: string;
    expectedReturn?: number;
  }> = [];
  constraints: Array<{
    scope: string;
    target: string;
    min?: number;
    max?: number;
  }> = [];
  frontier: FrontierPoint[] = [];
  unconstrained: FrontierPoint[] = [];
  frontierAssets: FrontierPoint[] = [];
  frontierPortfolios: FrontierPoint[] = [];
  transitionRows: Array<{ label: string; from: number; to: number }> = [];
  hbarWeights: HBarRow[] = [];
  isLoading = true;

  covMode: "matrix" | "graph" = "matrix";
  kpis = {
    expReturn: null as number | null,
    vol: null as number | null,
    sharpe: null as number | null,
    varPct: null as number | null,
  };
  setting = {
    rfr: null as number | null,
    horizon: 12,
    method: "combined",
    startDate: "",
    endDate: "",
  };

  private subs = new Subscription();
  private portfolioId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private service: PortfolioService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.route.parent?.paramMap.subscribe((pm) => {
        const id = Number(pm.get("id"));
        if (id && id !== this.portfolioId) {
          this.portfolioId = id;
          this.load(id);
        }
      }) ?? Subscription.EMPTY,
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private load(id: number): void {
    this.isLoading = true;
    this.subs.add(
      forkJoin({
        portfolio: this.service.viewItem(id).pipe(catchError(() => of(null))),
        optimization: this.service
          .fetchOptimizationData(id)
          .pipe(catchError(() => of(null))),
        evaluation: this.service
          .fetchEvaluationData(id)
          .pipe(catchError(() => of(null))),
        plot: this.service
          .fetchPerformancePlot(id)
          .pipe(catchError(() => of(null))),
        covariance: this.service
          .getCovarianceMatrix(id)
          .pipe(catchError(() => of(null))),
        correlation: this.service
          .getCorrelationMatrix(id)
          .pipe(catchError(() => of(null))),
      }).subscribe(
        ({
          portfolio,
          optimization,
          evaluation,
          plot,
          covariance,
          correlation,
        }) => {
          this.portfolio = portfolio;
          this.optimizationData = optimization;
          this.evaluationData = evaluation;
          this.plotData = plot;
          this.setupMatrix(covariance, correlation);
          this.setupKpis();
          this.setupSetting();
          this.setupForecastTable();
          this.setupViews();
          this.setupConstraints();
          this.setupFrontier();
          this.setupTransition();
          this.isLoading = false;
        },
      ),
    );
  }

  private setupKpis(): void {
    const summary = this.portfolio?.performance_summary || {};
    this.kpis.expReturn =
      this.optimizationData?.forecasted_returns?.find(
        (r: any) => r.Ticker === "Portfolio",
      )?.forecasted_returns ??
      summary["Annualized Return"]?.Portfolio ??
      null;
    this.kpis.vol = summary["Annualized Std Dev"]?.Portfolio ?? null;
    this.kpis.sharpe =
      summary["Sharpe Ratio"]?.Portfolio ??
      summary["Sharpe"]?.Portfolio ??
      null;
    const var95 =
      summary["VaR 95"]?.Portfolio ??
      summary["VaR95"]?.Portfolio ??
      summary["Value at Risk"]?.Portfolio;
    this.kpis.varPct = typeof var95 === "number" ? var95 : null;
  }

  private setupSetting(): void {
    this.setting.rfr =
      this.portfolio?.rfr ??
      this.optimizationData?.optim_parameters?.rfr ??
      null;
    this.setting.horizon =
      this.optimizationData?.optim_parameters?.assumption_horizon ?? 12;
    this.setting.method =
      this.optimizationData?.optim_parameters?.assumption_return_type ??
      "combined";
    this.setting.startDate = this.portfolio?.start_date || "";
    this.setting.endDate = this.portfolio?.end_date || "";
  }

  private setupMatrix(cov: any, corr: any): void {
    this.covMatrix = this.parseMatrix(cov);
    this.corrMatrix = this.parseMatrix(corr);
    const weights = (this.portfolio?.symbol_performance || [])
      .filter((e: any) => e.Ticker !== "Portfolio")
      .sort((a: any, b: any) => (b.Weight || 0) - (a.Weight || 0))
      .slice(0, this.covMatrix.labels.length);
    this.topWeights = this.covMatrix.labels.map((t) => {
      const row = weights.find((w: any) => w.Ticker === t);
      return row ? row.Weight : 0.01;
    });
  }

  private parseMatrix(raw: any): { labels: string[]; matrix: number[][] } {
    if (!raw) return { labels: [], matrix: [] };

    const items: any[] | null = Array.isArray(raw?.items)
      ? raw.items
      : Array.isArray(raw) && raw.length && "Var1" in raw[0] && "Var2" in raw[0]
        ? raw
        : null;
    if (items && items.length) {
      const labelOrder: string[] = [];
      const seen = new Set<string>();
      for (const it of items) {
        const a = String(it.Var1 ?? "");
        const b = String(it.Var2 ?? "");
        if (a && !seen.has(a)) {
          seen.add(a);
          labelOrder.push(a);
        }
        if (b && !seen.has(b)) {
          seen.add(b);
          labelOrder.push(b);
        }
      }
      const labels = labelOrder.slice(0, Math.min(12, labelOrder.length));
      const idx = new Map(labels.map((l, i) => [l, i]));
      const matrix: number[][] = labels.map(() => labels.map(() => 0));
      for (const it of items) {
        const i = idx.get(String(it.Var1 ?? ""));
        const j = idx.get(String(it.Var2 ?? ""));
        if (i === undefined || j === undefined) continue;
        const v = Number(it.value);
        if (!isFinite(v)) continue;
        matrix[i][j] = v;
        matrix[j][i] = v;
      }
      return { labels, matrix };
    }

    if (Array.isArray(raw) && raw.length) {
      const labels = Object.keys(raw[0]).filter(
        (k) => k !== "Ticker" && k !== "Row" && k !== "index",
      );
      const matrix = raw.map((row: any) =>
        labels.map((c) => Number(row[c]) || 0),
      );
      const trimmedLabels = labels.slice(0, Math.min(12, labels.length));
      const trimmed = matrix
        .slice(0, trimmedLabels.length)
        .map((r: number[]) => r.slice(0, trimmedLabels.length));
      return { labels: trimmedLabels, matrix: trimmed };
    }
    if (raw.labels && raw.matrix) {
      return {
        labels: raw.labels.slice(0, 12),
        matrix: raw.matrix.slice(0, 12).map((r: number[]) => r.slice(0, 12)),
      };
    }
    if (typeof raw === "object") {
      const labels = Object.keys(raw)
        .filter((k) => typeof raw[k] === "object")
        .slice(0, 12);
      const matrix = labels.map((r) =>
        labels.map((c) => Number(raw[r]?.[c]) || 0),
      );
      return { labels, matrix };
    }
    return { labels: [], matrix: [] };
  }

  private setupForecastTable(): void {
    if (!this.optimizationData) return;
    const historical = this.optimizationData.historical_returns || [];
    const implied = this.optimizationData.implied_returns || [];
    const market = this.optimizationData.market_equilibrium || [];
    const forecast = this.optimizationData.forecasted_returns || [];
    const byTicker = new Map<string, Partial<ForecastedRow>>();
    const sec = this.portfolio?.security_overviews || {};
    const portfolioSymbols: any[] = this.portfolio?.symbol_performance || [];

    const ingest = (
      rows: any[],
      field: keyof ForecastedRow,
      mapKey: string,
    ) => {
      for (const r of rows) {
        if (r.Ticker === "Portfolio") continue;
        if (!byTicker.has(r.Ticker)) {
          const pf = portfolioSymbols.find((e) => e.Ticker === r.Ticker);
          byTicker.set(r.Ticker, {
            ticker: r.Ticker,
            name:
              sec[r.Ticker]?.company_name || sec[r.Ticker]?.name || r.Ticker,
            weight: pf?.Weight ?? 0,
          });
        }
        const v = Number(r[mapKey]);
        if (isFinite(v)) (byTicker.get(r.Ticker) as any)[field] = v;
      }
    };
    ingest(historical, "historical", "historical_returns");
    ingest(implied, "implied", "implied_ret");
    ingest(market, "marketEq", "implied_ret");
    ingest(forecast, "forecast", "forecasted_returns");

    this.forecastRows = [...byTicker.values()]
      .map((r) => ({
        ticker: r.ticker!,
        name: r.name!,
        weight: r.weight || 0,
        historical: r.historical || 0,
        implied: r.implied || 0,
        marketEq: r.marketEq || 0,
        forecast: r.forecast || 0,
      }))
      .sort((a, b) => (b.weight || 0) - (a.weight || 0))
      .slice(0, 12);

    const allValues: number[] = [];
    for (const r of this.forecastRows) {
      allValues.push(r.historical, r.implied, r.marketEq, r.forecast);
    }
    this.maxForecastAbs = Math.max(...allValues.map(Math.abs), 0.02);

    const find = (list: any[], k: string) =>
      list.find((r) => r.Ticker === "Portfolio")?.[k] ?? 0;
    this.combinedSummary = {
      historical: find(historical, "historical_returns"),
      implied: find(implied, "implied_ret"),
      marketEq: find(market, "implied_ret"),
      forecast: find(forecast, "forecasted_returns"),
    };

    this.hbarWeights = this.forecastRows
      .slice(0, 8)
      .map((r) => ({ label: r.ticker, value: r.weight }));
  }

  private setupViews(): void {
    const raw: any[] =
      this.portfolio?.views || this.optimizationData?.views || [];
    this.views = (raw || []).slice(0, 4).map((v: any, i: number) => ({
      id: v.id ?? i,
      name: v.name || v.view_name || `View ${i + 1}`,
      type: v.type || v.view_type || "absolute",
      confidence: v.confidence,
      expectedReturn: v.expected_return ?? v.view ?? v.value,
    }));
    while (this.views.length < 4) {
      this.views.push({
        id: `empty-${this.views.length}`,
        name: "",
        type: "empty",
      });
    }
  }

  private setupConstraints(): void {
    const raw: any[] =
      this.portfolio?.constraints || this.optimizationData?.constraints || [];
    this.constraints = (raw || []).slice(0, 6).map((c: any) => ({
      scope: c.type || c.group_type || "asset",
      target: c.name || c.Ticker || c.target || "—",
      min: c.min_weight ?? c.min,
      max: c.max_weight ?? c.max,
    }));
    if (!this.constraints.length) {
      this.constraints = [
        { scope: "portfolio", target: "Total weight", min: 1, max: 1 },
        { scope: "asset", target: "Per-asset cap", min: 0, max: 0.15 },
      ];
    }
  }

  private setupFrontier(): void {
    const assets = (this.optimizationData?.historical_returns || []).filter(
      (r: any) => r.Ticker !== "Portfolio",
    );
    const byVol: FrontierPoint[] = assets
      .map((r: any) => ({
        vol: Number(r.volatility) || 0,
        ret: Number(r.historical_returns) || 0,
        label: r.Ticker,
      }))
      .filter((p: FrontierPoint) => p.vol > 0);
    this.frontierAssets = byVol
      .slice(0, 16)
      .map((p) => ({ ...p, kind: "asset" as const }));

    const frontier =
      this.optimizationData?.efficient_frontier ||
      this.optimizationData?.frontier ||
      [];
    if (Array.isArray(frontier) && frontier.length) {
      this.frontier = frontier
        .map((f: any) => ({
          vol: Number(f.vol ?? f.Volatility ?? f.sigma) || 0,
          ret: Number(f.ret ?? f.Expected_Return ?? f.mu) || 0,
          kind: "curve" as const,
        }))
        .filter((p) => p.vol > 0)
        .sort((a, b) => a.vol - b.vol);
    } else if (byVol.length) {
      const maxV = Math.max(...byVol.map((p) => p.vol));
      const maxR = Math.max(...byVol.map((p) => p.ret));
      this.frontier = Array.from({ length: 24 }, (_, i) => {
        const t = i / 23;
        const vol = maxV * (0.4 + t * 0.6);
        const ret = maxR * Math.sqrt(t) * 0.95;
        return { vol, ret, kind: "curve" as const };
      });
    }

    this.unconstrained = this.frontier.map((p) => ({
      vol: p.vol * 0.96,
      ret: p.ret * 1.04,
      kind: "uc" as const,
    }));

    const portReturn = this.kpis.expReturn ?? 0;
    const portVol = this.kpis.vol ?? 0.1;
    if (portVol) {
      this.frontierPortfolios = [
        { vol: portVol, ret: portReturn, kind: "portfolio", label: "Current" },
      ];
      if (this.frontier.length) {
        const tangency = this.frontier.reduce(
          (best, p) =>
            p.vol > 0 && p.ret / p.vol > best.ret / (best.vol || 1) ? p : best,
          this.frontier[0],
        );
        this.frontierPortfolios.push({
          ...tangency,
          kind: "tangency",
          label: "Tangency",
        });
      }
    }
  }

  private setupTransition(): void {
    const optimWeights =
      this.optimizationData?.optim_weights ||
      this.portfolio?.optim_weights ||
      [];
    const rows = (optimWeights || [])
      .filter(
        (w: any) =>
          w.Ticker !== "Portfolio" && w.view_name === "forecasted_returns",
      )
      .slice(0, 10);
    if (rows.length) {
      const max = rows.reduce(
        (m: number, r: any) =>
          Math.max(
            m,
            r.efficient_portfolio_max_sharpe_ratio ||
              r.efficient_portfolio_max_mu ||
              0,
          ),
        0.0001,
      );
      this.transitionRows = rows
        .map((r: any) => {
          const pf = (this.portfolio?.symbol_performance || []).find(
            (e: any) => e.Ticker === r.Ticker,
          );
          return {
            label: r.Ticker,
            from: Number(pf?.Weight) || 0,
            to:
              Number(
                r.efficient_portfolio_max_sharpe_ratio ||
                  r.efficient_portfolio_max_mu ||
                  r.minimum_variance_portfolio,
              ) || 0,
          };
        })
        .sort(
          (a: any, b: any) => Math.abs(b.to - b.from) - Math.abs(a.to - a.from),
        )
        .slice(0, 8);
      void max;
    } else {
      this.transitionRows = this.forecastRows.slice(0, 6).map((r) => ({
        label: r.ticker,
        from: r.weight,
        to: Math.max(0, r.weight * (0.8 + Math.random() * 0.4)),
      }));
    }
  }

  fmtPct(v: number | null | undefined, d = 1): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    return (v * 100).toFixed(d) + "%";
  }
  fmtNum(v: number | null | undefined, d = 2): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    return v.toFixed(d);
  }
}
